import React, { useEffect, useState, useContext } from "react";
import firebase from "firebase/app";
import "firebase/auth";
import initFirebase from "utils/auth/initFirebase";
import { setUserCookie } from "utils/auth/userCookies";
import { mapUserData } from "utils/auth/mapUserData";
import { Box, Button, Input, Flex } from "@chakra-ui/core";
import { ContextStore } from "context/ContextStore";
import { useRouter } from "next/router";
import CustomLink from "components/shared/CustomLink";

// Init the Firebase app.
initFirebase();

const AuthBox = () => {
  const [authProcess, setAuthProccess] = useState(undefined);

  const router = useRouter();

  const [emailInput, setEmailInput] = useState("");
  const [passInput, setPassInput] = useState("");
  const [displayNameInput, setDisplayNameInput] = useState("");
  // const [lockEmailInput, setLockEmailInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { basketOrders, showToast, handleError } = useContext(ContextStore);

  useEffect(() => {
    router.query.email && setEmailInput(router.query.email);
  }, [router.query.email]);

  const handleLogIn = (email, pass) => {
    setIsLoading(true);
    firebase
      .auth()
      .signInWithEmailAndPassword(email, pass)
      .then((response) => {
        const userData = mapUserData(response.user);
        setUserCookie(userData);

        response.user
          .getIdTokenResult()
          .then((idTokenResult) => {
            // Confirm the user is an Admin.
            if (!!idTokenResult.claims.administrator) {
              showToast({
                title: "Connexion réussie !",
              });
              router.push("/admin"); // Redirect
            } else {
              showToast({
                title: "Connexion réussie !",
              });
              router.push(basketOrders.length === 0 ? "/" : "/paiement");
            }
          })
          .catch((error) => {
            handleError({ title: "Erreur de connexion", error });
          });
      })
      .catch((error) => {
        setIsLoading(false);
        handleError({ title: "Erreur de connexion", error });
      });
  };

  const handleSignUp = (email, pass) => {
    setIsLoading(true);
    firebase
      .auth()
      .createUserWithEmailAndPassword(email, pass)
      .then((response) => {
        const userData = mapUserData(response.user);
        setUserCookie(userData);

        firebase.firestore().collection("users").doc(userData.id).set({ email: userData.email }, { merge: true });

        response.user
          .updateProfile({
            displayName: displayNameInput,
          })
          .then(() => {
            router.push("/connexion/numero");
            showToast({
              title: "Inscription presque terminée",
              body: "Ajoutez un numéro de téléphone pour finaliser votre compte",
            });
          })
          .catch((error) => {
            setIsLoading(false);
            handleError({ title: "Erreur de connexion", error });
          });
      })
      .catch((error) => {
        setIsLoading(false);
        handleError({ title: "Erreur de connexion", error });
      });
  };

  const handleCheckEmail = (email) => {
    setIsLoading(true);
    // setLockEmailInput(true);
    firebase
      .auth()
      .fetchSignInMethodsForEmail(email)
      .then((response) => {
        setIsLoading(false);
        if (response.length === 0) {
          setAuthProccess("signup");
        } else {
          setAuthProccess("login");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        // setLockEmailInput(false);
        handleError({ title: "Erreur de connexion", error });
      });
  };

  const isEmailValid = emailInput.includes("@") && emailInput.includes(".");
  const isPasswordValid = passInput.length > 3;
  const isDisplayNameValid = displayNameInput.length >= 2;
  const isSignUpCredentialsValid = isEmailValid && isPasswordValid && isDisplayNameValid;
  const isLoginCredentialsValid = isEmailValid && isPasswordValid;

  return (
    <>
      <Flex align="center" justify="center">
        <Box px="6" pt="6" pb="8" w="full" bg="white" border="1px" borderColor="gray.200" rounded="md" maxW="400px">
          <Box
            display="block"
            as="span"
            fontSize="xl"
            fontWeight="semibold"
            borderBottom="1px"
            borderColor="gray.200"
            mb="4"
            pb="2"
          >
            {authProcess === undefined && "Entrez votre email"}
            {authProcess === "signup" && "Créez un compte"}
            {authProcess === "login" && "Connectez-vous"}
          </Box>

          <Box as="span" fontWeight="semibold">
            Email
          </Box>
          <Input
            placeholder="Email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            type="email"
            // isReadOnly={lockEmailInput}
            isRequired
            mt="2"
            mb="4"
          />
          {authProcess === "login" && (
            <>
              <Box as="span" fontWeight="semibold">
                Mot de passe
              </Box>
              <Input
                isRequired={authProcess === "login"}
                placeholder="Mot de passe"
                type="password"
                value={passInput}
                onChange={(e) => setPassInput(e.target.value)}
                mt="2"
                mb="2"
              />

              <Box
                as={CustomLink}
                href={`/connexion/oubli?email=${emailInput}`}
                fontSize="sm"
                fontWeight="500"
                mb="4"
                color="gray.600"
              >
                Mot de passe oublié ?
              </Box>
            </>
          )}

          {authProcess === "signup" && (
            <>
              <Box as="span" fontWeight="semibold">
                Prénom
              </Box>
              <Input
                placeholder="Prénom"
                type="text"
                value={displayNameInput}
                onChange={(e) => setDisplayNameInput(e.target.value)}
                isRequired={authProcess === "signup"}
                mt="2"
                mb="4"
              />

              <Box as="span" fontWeight="semibold">
                Mot de passe
              </Box>
              <Input
                placeholder="Mot de passe"
                type="password"
                isRequired={authProcess === "signup"}
                value={passInput}
                onChange={(e) => setPassInput(e.target.value)}
                mt="2"
                mb="8"
              />
            </>
          )}

          <Flex align="center" justify="flex-end">
            {authProcess === undefined && (
              <Button
                variantColor="blue"
                disabled={!isEmailValid || isLoading}
                rounded="lg"
                shadow="lg"
                onClick={() => handleCheckEmail(emailInput)}
              >
                Suivant
              </Button>
            )}
            {authProcess === "login" && (
              <Button
                variantColor="blue"
                disabled={!isLoginCredentialsValid || isLoading}
                rounded="lg"
                shadow="lg"
                onClick={() => handleLogIn(emailInput, passInput)}
              >
                Se connecter
              </Button>
            )}
            {authProcess === "signup" && (
              <Button
                variantColor="blue"
                disabled={!isSignUpCredentialsValid || isLoading}
                rounded="lg"
                shadow="lg"
                onClick={() => handleSignUp(emailInput, passInput)}
              >
                S'inscrire
              </Button>
            )}
          </Flex>
        </Box>
      </Flex>
    </>
  );
};

export default AuthBox;
